import React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { Diamond, Clock, ArrowRight } from "lucide-react";
import type { HomepageArticle } from "@/components/homepage/types";

interface FeaturedInvestigationSectionProps {
  article: HomepageArticle | null;
  related: HomepageArticle[];
  isLoading: boolean;
}

export function FeaturedInvestigationSection({ article, related, isLoading }: FeaturedInvestigationSectionProps) {
  const hasData = article || related.length > 0;

  if (!hasData && !isLoading) return null;

  return (
    <section aria-labelledby="investigation-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          eyebrow="Investigation"
          icon={Diamond}
          title="Featured Investigation"
          href="/discover"
        />
        {isLoading ? (
          <div className="grid gap-10 lg:grid-cols-5">
            <div className="lg:col-span-3"><SectionLoader variant="hero" /></div>
            <div className="lg:col-span-2 space-y-4"><SectionLoader variant="list" count={3} /></div>
          </div>
        ) : hasData ? (
          <div className="grid gap-10 lg:grid-cols-5">
            {article && (
              <article className="group cursor-pointer lg:col-span-3">
                <Link href={`/news/${article.id}`} className="block">
                  <div className="aspect-[16/9] bg-neutral-100 rounded-xl mb-5 overflow-hidden border border-border/50">
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground/20 bg-neutral-50">
                      <Diamond className="h-16 w-16" />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{article.readTime ?? "10 min read"}</span>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-heading font-semibold leading-snug text-foreground group-hover:text-primary transition-colors duration-200">
                      {article.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                      {typeof article.content === "string" ? article.content.slice(0, 300) : ""}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
                      <span className="font-medium text-foreground">{article.author}</span>
                      <span className="text-neutral-300">·</span>
                      <span>{article.createdAt}</span>
                    </div>
                  </div>
                </Link>
              </article>
            )}
            <div className="lg:col-span-2">
              <h4 className="eyebrow mb-6">Related</h4>
              <div className="space-y-0">
                {related.map((item, idx) => (
                  <article key={item.id} className="group border-t border-border/60 first:border-t-0">
                    <Link href={`/news/${item.id}`} className="flex items-start gap-4 py-4 group">
                      <span className="font-display text-2xl font-semibold text-neutral-200 shrink-0 tabular-nums w-8 text-center leading-none pt-1">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-heading font-semibold leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2">
                          {item.title}
                        </h4>
                        <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{item.author}</span>
                          <span className="text-neutral-300">·</span>
                          <span>{item.createdAt}</span>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <EmptyState icon={Diamond} description="No investigation available yet." />
        )}
      </div>
    </section>
  );
}